/**
 * Device Card Component - Individual device card view
 * 
 * AI Agent Note: This component displays a single device's details in card format.
 * Uses icons and labels from config/constants.
 */

import React from 'react';
import { Card, Flex, Text, Button, Badge, Box, Separator } from '@radix-ui/themes';
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { Device } from '../../types';
import { DEVICE_TYPE_LABELS } from '../../config/constants';
import DeviceIcon from '../DeviceIcon';
import { useDevices } from '../../contexts/DeviceContext';
import { calculateAmps, formatAmps, getDeviceVoltage, isPassthroughDevice, calculateTotalPowerDraw, formatPower } from '../../utils/powerCalculations';
import './DeviceCard.css';

interface DeviceCardProps {
  device: Device;
  onEdit: (device: Device) => void;
  hasIssue?: boolean;
  issueMessage?: string;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ device, onEdit, hasIssue, issueMessage }) => {
  const { deleteDevice, getDeviceById, getConnectedDevices, devices } = useDevices();
  const parentDevice = device.connectedTo ? getDeviceById(device.connectedTo) : null;
  const connectedChildren = getConnectedDevices(device.id);
  const voltage = getDeviceVoltage(device);
  const availableAmps = device.passthroughPower > 0 ? calculateAmps(device.passthroughPower, voltage) : 0;
  
  // Calculate actual self-consumption for passthrough devices
  const isPassthrough = isPassthroughDevice(device);
  let deviceSelfPower = device.powerRequirement;
  
  if (isPassthrough && connectedChildren.length > 0) {
    // Calculate children's total power draw
    const childrenPower = connectedChildren.reduce((sum, child) => {
      return sum + calculateTotalPowerDraw(child.id, devices);
    }, 0);
    deviceSelfPower = childrenPower * 0.01; // 1% overhead
  }
  
  const ownAmps = calculateAmps(deviceSelfPower, voltage);

  const handleDelete = () => {
    if (window.confirm(`Delete ${device.name}?`)) {
      deleteDevice(device.id);
    }
  };

  return (
    <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Flex direction="column" gap="3" style={{ flex: 1 }}>
        {/* Header with icon and name */}
        <Flex align="center" gap="2">
          <DeviceIcon type={device.type} size={48} />
          <Box style={{ flex: 1 }}>
            <Text size="4" weight="bold" style={{ display: 'block', lineHeight: '1.3' }}>
              {device.name}
            </Text>
            <Badge color="violet" variant="soft" size="1">
              {DEVICE_TYPE_LABELS[device.type]}
            </Badge>
          </Box>
        </Flex>

        {hasIssue && issueMessage && (
          <Card size="1" style={{ backgroundColor: 'var(--red-3)', borderColor: 'var(--red-7)' }}>
            <Flex gap="2" align="start">
              <Text>⚠️</Text>
              <Text size="1" style={{ flex: 1 }}>{issueMessage}</Text>
            </Flex>
          </Card>
        )}

        {/* Power Specifications */}
        <Box>
          <Flex direction="column" gap="2">
            {(device.type === 'power-source' || device.type === 'power-box') && device.passthroughPower > 0 ? (
              <>
                <Flex justify="between" align="center">
                  <Text size="2" color="gray">Available Power</Text>
                  <Flex gap="2">
                    <Badge color="green" size="2">{device.passthroughPower}W</Badge>
                    <Badge color="blue" size="2">{device.maxTotalCurrent ? `${device.maxTotalCurrent}A` : `${formatAmps(availableAmps)}A`}</Badge>
                  </Flex>
                </Flex>
                {device.maxTotalCurrent && device.maxTotalCurrent > 0 && (
                  <Flex justify="between" align="center">
                    <Text size="2" color="gray">Total Current Limit</Text>
                    <Badge color="cyan" variant="soft" size="2">{device.maxTotalCurrent}A shared</Badge>
                  </Flex>
                )}
                {deviceSelfPower > 0 && (
                  <Flex justify="between" align="center">
                    <Text size="2" color="gray">Self Consumption</Text>
                    <Flex gap="2">
                      <Badge color="gray" variant="soft" size="2">{formatPower(deviceSelfPower)}W</Badge>
                      <Badge color="gray" variant="soft" size="2">{formatAmps(ownAmps)}A</Badge>
                    </Flex>
                  </Flex>
                )}
              </>
            ) : (
              <Flex justify="between" align="center">
                <Text size="2" color="gray">Power Draw</Text>
                <Flex gap="2">
                  <Badge color="orange" size="2">{formatPower(device.powerRequirement)}W</Badge>
                  <Badge color="blue" size="2">{formatAmps(ownAmps)}A</Badge>
                  <Badge color="gray" variant="soft" size="2">@ {voltage}V</Badge>
                </Flex>
              </Flex>
            )}
          </Flex>
        </Box>

        {/* USB Ports */}
        {(device.usbInputs && device.usbInputs.length > 0) || (device.usbOutputs && device.usbOutputs.length > 0) ? (
          <Box>
            <Text size="2" weight="bold" color="gray" style={{ display: 'block', marginBottom: '8px' }}>USB Ports</Text>
            <Flex direction="column" gap="1">
              {device.usbInputs && device.usbInputs.map((input, idx) => (
                <Flex key={`in-${idx}`} align="center" gap="2">
                  <Badge color="violet" variant="soft" size="1">IN</Badge>
                  <Text size="1">{input.count}× {input.version} {input.powered ? '(Powered)' : ''}</Text>
                </Flex>
              ))}
              {device.usbOutputs && device.usbOutputs.map((output, idx) => (
                <Flex key={`out-${idx}`} align="center" gap="2">
                  <Badge color="green" variant="soft" size="1">OUT</Badge>
                  <Text size="1">{output.count}× {output.version} {output.powered ? '(Powered)' : ''}</Text>
                </Flex>
              ))}
            </Flex>
          </Box>
        ) : null}

        {/* Connections */}
        {(parentDevice || connectedChildren.length > 0) && (
          <Box>
            <Flex direction="column" gap="1">
              {parentDevice && (
                <Flex align="center" gap="2">
                  <Text size="1" color="gray">↑ From:</Text>
                  <Text size="1" weight="medium">{parentDevice.name}</Text>
                </Flex>
              )}
              {connectedChildren.length > 0 && (
                <Flex align="center" gap="2">
                  <Text size="1" color="gray">↓ Powers:</Text>
                  <Text size="1" weight="medium">{connectedChildren.length} device{connectedChildren.length > 1 ? 's' : ''}</Text>
                </Flex>
              )}
            </Flex>
          </Box>
        )}

        {/* Notes */}
        {device.description && (
          <Box>
            <Text size="1" color="gray" style={{ fontStyle: 'italic' }}>
              {device.description}
            </Text>
          </Box>
        )}

        {/* Spacer to push buttons to bottom */}
        <Box style={{ flex: 1 }} />

        {/* Action Buttons at Bottom */}
        <Box>
          <Separator size="4" mb="3" />
          <Flex gap="2" justify="end">
            <Button 
              variant="soft" 
              color="gray"
              size="2"
              onClick={() => onEdit(device)}
            >
              <Pencil1Icon /> Edit
            </Button>
            <Button 
              variant="soft" 
              color="red"
              size="2"
              onClick={handleDelete}
            >
              <TrashIcon /> Delete
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Card>
  );
};

export default DeviceCard;
